import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PasswordTextField from '../components/PasswordTextField';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL ?? '';

const FirstLoginPassword: React.FC = () => {
  const { token, user, setAuthFromTokenResponse } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (user && !user.mustChangePassword) {
      navigate('/', { replace: true });
    }
  }, [navigate, user]);

  const validateForm = () => {
    if (!password || !confirmPassword) {
      setError('すべての項目を入力してください。');
      return false;
    }
    if (password !== confirmPassword) {
      setError('パスワードが一致しません。');
      return false;
    }
    if (password.length < 8) {
      setError('パスワードは8文字以上である必要があります。');
      return false;
    }

    const hasLetter = /[a-zA-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSymbol = /[^A-Za-z0-9]/.test(password);
    if ([hasLetter, hasNumber, hasSymbol].filter(Boolean).length < 2) {
      setError('パスワードは英字・数字・記号のうち、少なくとも2種類を含む必要があります。');
      return false;
    }

    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    if (!token) {
      setError('認証情報が見つかりません。再度ログインしてください。');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/auth/first-login-password`,
        { newPassword: password },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const isSessionReady = await setAuthFromTokenResponse(response.data);
      if (!isSessionReady) {
        setError('セッション確認に失敗しました。再ログインが必要です。');
        return;
      }
      navigate('/', { replace: true });
    } catch (err: any) {
      if (err.response?.data?.detail === 'password_complexity_invalid') {
        setError('パスワードは英字・数字・記号のうち、少なくとも2種類を含む必要があります。');
      } else {
        setError('パスワードの変更に失敗しました。時間をおいて再度お試しください。');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" component="h1" align="center" gutterBottom>
          初回パスワード変更
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          初回ログインのため、利用を開始する前に新しいパスワードを設定してください。
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          試用期間のカウントは、このパスワード変更が完了した時点から開始されます。
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <PasswordTextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="新しいパスワード"
            type="password"
            id="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isSubmitting}
            helperText="8文字以上で、英字・数字・記号のうち2種類以上を含む必要があります"
          />
          <PasswordTextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="パスワード（確認）"
            type="password"
            id="confirmPassword"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={isSubmitting}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, py: 1.5 }}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              'パスワードを変更'
            )}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default FirstLoginPassword;
